import React, { useEffect, useMemo } from 'react';

import { Cookies } from '@imgly/imgly-website-components';
import { get } from 'lodash';
// @ts-ignore
import Prism from 'prism-react-renderer/prism';
import { Helmet } from 'react-helmet';
import { useScrollbarWidth } from 'react-use';

import { Data, Options, PageContextType } from '../../../types';
import { PageContext } from '../../context/PageContext';
import { SessionCodeLanguageContext } from '../../context/SessionCodeLanguageContext';
import { useSessionCodeLanguage } from '../../hooks/useSessionCodeLanguages';
import { ContentManager } from '../ContentManager';
import { DocumentationNavigation } from '../DocumentationNavigation';
import { Footer } from '../Footer';
import { FreddyFeedback } from '../FreddyFeedback';
import { Header } from '../Header';
import { SearchModal } from '../SearchModal';
import { SEO } from '../SEO';
import { SkipToContent } from '../SkipToContent';
import '../../styles/root.scss';

import * as classes from './style.module.scss';

declare const window: any;

// eslint-disable-next-line no-underscore-dangle
declare const __PATH_PREFIX__: undefined | string;

// add language support here
(typeof global !== 'undefined' ? global : window).Prism = Prism;

require('prismjs/components/prism-kotlin');
require('prismjs/components/prism-swift');
require('prismjs/components/prism-groovy');
require('prismjs/components/prism-java');
require('prismjs/components/prism-dart');

interface Props {
  data: Data;
  children: React.ReactNode;
  location: Location;
  pageContext: PageContextType;
  pluginOptions: Options;
}

export const Layout: React.FC<Props> = ({
  data,
  children,
  location,
  pageContext,
  pluginOptions,
}) => {
  const sbw = useScrollbarWidth();

  const { pathname } = location;

  useEffect(() => {
    document.documentElement.style.setProperty('--scrollbar-width', `${sbw}px`);
  }, [sbw]);

  const { title, metaTitle, product, canonical, platform, platforms } =
    pageContext;
  const {
    algoliaApiKey,
    algoliaIndexName,
    algoliaAppId,
    ffWidgetId,
    siteName,
    siteImage,
    description: siteDescription,
  } = pluginOptions;
  const description =
    data?.file?.childMdx?.frontmatter?.description || siteDescription;

  const [sessionLanguages, setSessionLanguage, setSessionLanguages] =
    useSessionCodeLanguage();

  const getTitleTemplate = (productName: string | undefined) => {
    switch (productName) {
      case 'pesdk':
        return `%s - ${metaTitle || title} - PE.SDK | ${siteName}`;
      case 'vesdk':
        return `%s - ${metaTitle || title} - VE.SDK | ${siteName}`;
      case 'cesdk':
        return `%s - CE.SDK | ${siteName}`;
      default:
        return `%s - ${siteName}`;
    }
  };

  const sessionCodeLanguageContextValue = useMemo(
    () => ({ sessionLanguages, setSessionLanguage }),
    [sessionLanguages, setSessionLanguage],
  );

  return (
    <>
      <Helmet
        htmlAttributes={{ lang: 'en' }}
        defaultTitle={siteName}
        titleTemplate={getTitleTemplate(product)}
      >
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=5"
        />
      </Helmet>
      <SEO
        canonical={canonical}
        platform={platform}
        platforms={platforms}
        frontmatter={data?.file?.childMdx?.frontmatter}
        siteName={siteName}
        siteImage={siteImage}
        description={description}
      />
      <FreddyFeedback ffWidgetId={ffWidgetId} />
      <SkipToContent />
      <PageContext.Provider value={pageContext}>
        <Cookies
          plausibleSettings={{
            props: {
              source:
                // __PATH_PREFIX__ is available once the docs are build and not during dev mode
                typeof __PATH_PREFIX__ !== undefined && __PATH_PREFIX__
                  ? __PATH_PREFIX__
                  : 'docs',
            },
          }}
        >
          <Header />
          <ContentManager
            values={data?.file?.childMdx?.fields?.contentSwitcherValues || ''}
            setSessionLanguages={setSessionLanguages}
          >
            <SessionCodeLanguageContext.Provider
              value={sessionCodeLanguageContextValue}
            >
              <div className={classes.wrapper}>
                <DocumentationNavigation
                  siteName={siteName}
                  pathname={pathname}
                />
                {children}
              </div>
            </SessionCodeLanguageContext.Provider>
            <SearchModal
              apiKey={algoliaApiKey}
              indexName={algoliaIndexName}
              appId={algoliaAppId}
            />
          </ContentManager>
        </Cookies>
        <Footer />
      </PageContext.Provider>
    </>
  );
};
